// =============================================================
// MUNDALO - Single source of truth for tier-based feature gating.
// All UI tier-gates and backend role guards should reference this.
// =============================================================

// --- Tier roles (matches backend `user.role` enum) ---
export const ROLES = {
  FREE_TRIAL:    'free_trial',     // 15 days, card required, full THE MOVE
  EXPIRED_TRIAL: 'expired_trial',  // post-trial lockout
  BLUEPRINT_ONLY:'blueprint_only', // $49 one-time - Blueprint AI generation only
  ESSENTIALS:    'essentials',     // $349 one-time
  GUIDED:        'guided',         // $549 one-time - PRIMARY upsell
  COMMUNITY:     'community',      // $9.99/mo or $99/yr - THE LIFE Community
  CONNECTED:     'connected',      // $19.99/mo or $199/yr - THE LIFE Connected
  ADMIN:         'admin',
  // Legacy FRG aliases - preserved so existing user docs still work
  CLIENT:           'client',
  COMMUNITY_MEMBER: 'community_member',
  BUNDLE_MONTHLY:   'bundle_monthly',
  BUNDLE_ANNUAL:    'bundle_annual',
};

// --- Tier hierarchy (numeric for comparisons) ---
const RANK = {
  [ROLES.EXPIRED_TRIAL]: -1,
  [ROLES.FREE_TRIAL]:     0,
  [ROLES.BLUEPRINT_ONLY]: 1,
  [ROLES.ESSENTIALS]:     2,
  [ROLES.GUIDED]:         3,
  [ROLES.COMMUNITY]:      4,
  [ROLES.CONNECTED]:      5,
  [ROLES.ADMIN]:          99,
  // Legacy
  [ROLES.CLIENT]:           3,
  [ROLES.COMMUNITY_MEMBER]: 4,
  [ROLES.BUNDLE_MONTHLY]:   5,
  [ROLES.BUNDLE_ANNUAL]:    5,
};

export const tierRank = (role) => RANK[role] ?? 0;

// --- Section assignment for nav grouping ---
export const SECTIONS = {
  MOVE: 'THE MOVE',  // Trial-gated; included with any paid tier
  LIFE: 'THE LIFE',  // Paid-only; some features Connected-only
};

// --- Feature catalog: id → metadata ---
// `allowedTiers` is the explicit whitelist of paid tiers that get access
// (admin + free_trial handled separately). This is more accurate than a
// pure rank check because the product has non-monotonic access (e.g.
// Community has events but NOT Blueprint, Blueprint Only has Blueprint
// but nothing else).
// FREE TRIAL ACCESS POLICY (Wave 2 - 2026-05-15)
// Per the new conversion strategy, the 15-day Free Trial is now the
// "community on-ramp" only. Trial members get the people, not the product.
// Open to free_trial: Overview · Newcomers Board · Community Board · Events
// Locked behind upgrade: Blueprint · Checklist · Classroom · Coaching ·
// Partners · Local Deals · Housing · City Guides · Connected-only.
export const FEATURES = {
  // ===== THE MOVE =====
  overview:  { section: 'MOVE', allowedTiers: 'all-paid+trial', preview: false, name: 'Overview',       path: '/portal/dashboard' },
  blueprint: { section: 'MOVE', allowedTiers: [ROLES.BLUEPRINT_ONLY, ROLES.ESSENTIALS, ROLES.GUIDED, ROLES.CLIENT], preview: false, name: 'Blueprint AI',   path: '/portal/blueprint', upgradeLabel: 'Unlock with Essentials →' },
  // Checklist - locked for trial (Wave 2). Was 'all-paid+trial'.
  checklist: { section: 'MOVE', allowedTiers: 'all-paid', preview: false, name: 'Checklist',      path: '/portal/checklist', upgradeLabel: 'Unlock with Essentials →' },
  newcomers: { section: 'MOVE', allowedTiers: 'all-paid+trial', preview: false, name: 'Newcomers Board',path: '/portal/community?board=newcomers' },
  communityBoard: { section: 'MOVE', allowedTiers: 'all-paid+trial', preview: false, name: 'Community Board', path: '/portal/community' },
  // Classroom - locked for trial (preview disabled in Wave 2; was preview:true).
  classroom: { section: 'MOVE', allowedTiers: [ROLES.ESSENTIALS, ROLES.GUIDED], preview: false,  name: 'Classroom',      path: '/portal/classroom', upgradeLabel: 'Unlock with Essentials →' },
  coaching:  { section: 'MOVE', allowedTiers: [ROLES.ESSENTIALS, ROLES.GUIDED], preview: false, name: 'Coaching',       path: '/portal/coaching', upgradeLabel: 'Unlock with Essentials →' },

  // ===== THE LIFE =====
  // Partners - locked for trial (Wave 2). Was 'all-paid+trial'.
  partners:       { section: 'LIFE', allowedTiers: 'all-paid', preview: false, name: 'Partners',         path: '/portal/partners' },
  // Local Deals (NEW): Groupon-style Mexico deals - Guided / Community / Connected
  localDeals:     { section: 'LIFE', allowedTiers: [ROLES.GUIDED, ROLES.COMMUNITY, ROLES.CONNECTED], preview: false, name: 'Local Deals',  path: '/portal/local-deals' },
  // Events - OPEN to trial (Wave 2). Trial = community on-ramp.
  events:         { section: 'LIFE', allowedTiers: [ROLES.FREE_TRIAL, ROLES.BLUEPRINT_ONLY, ROLES.ESSENTIALS, ROLES.GUIDED, ROLES.COMMUNITY, ROLES.CONNECTED], preview: false, name: 'Events',       path: '/portal/events' },
  housing:        { section: 'LIFE', allowedTiers: [ROLES.GUIDED, ROLES.COMMUNITY, ROLES.CONNECTED], preview: false, name: 'Housing',      path: '/portal/housing', upgradeLabel: 'Unlock with Guided →' },
  // Activities - hidden from sidebar (iter 50). Kept in FEATURES so any deep
  // links still resolve, but PortalLayout no longer renders the nav item.
  activities:     { section: 'LIFE', allowedTiers: [ROLES.GUIDED, ROLES.COMMUNITY, ROLES.CONNECTED], preview: false, name: 'Activities',   path: '/portal/events?type=activities', hidden: true },
  partnerDeals:   { section: 'LIFE', allowedTiers: [ROLES.COMMUNITY, ROLES.CONNECTED], preview: false, name: 'Partner Deals',path: '/portal/community?board=partners' },
  // Connected-tier only
  // City Guides - Wave 2.1 (iter 97): trial users get READ-ONLY catalog
  // access (page renders for them, buy CTA routes to /portal/upgrade).
  // Guided gets 3 auto-granted; other paid tiers buy at $4.99/guide.
  cityGuides:     { section: 'LIFE', allowedTiers: 'all-paid+trial', preview: false, name: 'City Guides',            path: '/portal/city-guides', upgradeLabel: 'Unlock with any paid plan →' },
  expertQa:       { section: 'LIFE', allowedTiers: [ROLES.CONNECTED], preview: false, name: 'Monthly Expert Q&A',    path: '/portal/events?type=qa',             connectedOnly: true },
  // iter 145 - Peer Circles hidden from sidebar/portal (Section 1).
  // Kept in FEATURES so any deep links still resolve gracefully.
  peerCircles:    { section: 'LIFE', allowedTiers: [ROLES.COMMUNITY, ROLES.CONNECTED], preview: false, name: 'Peer Circles', path: '/portal/peer-circles', upgradeLabel: 'Unlock with Community →', hidden: true },
  // Adventures - Viator affiliate surface (iter 79). Visible to ALL tiers
  // including free trial. Never paywalled; `preview: true` lets the trial
  // role through `canAccess`.
  adventures:     { section: 'LIFE', allowedTiers: [ROLES.COMMUNITY, ROLES.CONNECTED, ROLES.ESSENTIALS, ROLES.GUIDED, ROLES.BLUEPRINT_ONLY], preview: true, name: 'Adventures', path: '/portal/adventures' },
};

// --- Access check ---
// Resolves three flavors of `allowedTiers`:
//   • Array of tier ids - explicit whitelist
//   • 'all-paid+trial' - every paid tier AND free_trial (lockout for expired_trial)
//   • 'all-paid'       - every paid tier, NOT free_trial (Wave 2)
//   • undefined - allow everyone
const PAID_TIERS = [
  ROLES.BLUEPRINT_ONLY, ROLES.ESSENTIALS, ROLES.GUIDED,
  ROLES.COMMUNITY, ROLES.CONNECTED,
  ROLES.CLIENT, ROLES.COMMUNITY_MEMBER, ROLES.BUNDLE_MONTHLY, ROLES.BUNDLE_ANNUAL,
];

export const canAccess = (userRole, featureId) => {
  if (userRole === ROLES.ADMIN) return true;
  const f = FEATURES[featureId];
  if (!f || !f.allowedTiers) return true;
  if (f.allowedTiers === 'all-paid+trial') {
    return userRole === ROLES.FREE_TRIAL || PAID_TIERS.includes(userRole);
  }
  if (f.allowedTiers === 'all-paid') {
    return PAID_TIERS.includes(userRole);
  }
  return f.allowedTiers.includes(userRole);
};

export const isPreviewable = (userRole, featureId) => {
  const f = FEATURES[featureId];
  return !!(f && f.preview && !canAccess(userRole, featureId));
};

// --- Pricing tiers (display) ---
export const TIERS = {
  free:           { id: 'free',           name: 'Free Trial',     price: 0,     interval: '15 days',  cta: 'Start Free',          primary: false, description: 'Newcomers Board, Community Board, and Local Events. Try the people first - .' },
  blueprint_only: { id: 'blueprint_only', name: 'Blueprint Only', price: 199,   interval: 'one-time', cta: 'Get Blueprint',       primary: false, description: 'Personalized 19-section relocation Blueprint for your chosen city, with backup city comparisons and PDF download.' },
  essentials:     { id: 'essentials',     name: 'Essentials',     price: 349,   interval: 'one-time', cta: 'Choose Essentials',   primary: false, description: 'Everything in Blueprint Only, plus the full Classroom (6 modules), 3 City Guides, the vetted partner network, and housing listings access.' },
  guided:         { id: 'guided',         name: 'Guided',         price: 549,   interval: 'one-time', cta: 'Get Guided',          primary: true,  description: 'Everything in Essentials, plus all 15 City Guides, 3 strategy coaching calls, and 1 year of Community access included.', badge: 'MOST POPULAR' },
  community:      { id: 'community',      name: 'Community',      price: 9.99,  priceAnnual: 99,  interval: 'month', cta: 'Join Community',  primary: false, description: 'Community Board, Local Deals, Local Adventures, in-person and online Events, housing + roommate finder.' },
  connected:      { id: 'connected',      name: 'Connected',      price: 19.99, priceAnnual: 199, interval: 'month', cta: 'Coming Soon',     primary: false, description: 'Everything in Community + monthly expert Q&A.', badge: 'COMING SOON', comingSoon: true, hidden: true },
};

// Default selection when upgrade wall opens
export const DEFAULT_UPGRADE_TIER = 'guided';

// --- Trial helpers ---
export const isOnTrial = (user) => user?.role === ROLES.FREE_TRIAL;
export const daysRemainingInTrial = (user) => {
  // Backend stores the field as `trial_expiry_date` (ISO string). Older code
  // looked for `trial_expires_at` / `trial_ends_at` - accept all three so we
  // never silently return 0 because of a field-name drift.
  const iso =
    user?.trial_expiry_date ||
    user?.trial_expires_at ||
    user?.trial_ends_at;
  if (!iso) return 0;
  const expiry = new Date(iso).getTime();
  if (Number.isNaN(expiry)) return 0;
  const now = Date.now();
  return Math.max(0, Math.ceil((expiry - now) / (1000 * 60 * 60 * 24)));
};
